/**
 * Generic horizontal scroll row used by all sliding sections (category cards, filter shortcuts, benefit tiles).
 * Scrolling itself is native (overflow + scroll-snap, see component-scroller.css). This element only drives the
 * optional controls rendered by snippets/scroller-controls.liquid:
 *   [data-scroller-prev] / [data-scroller-next]  scroll by one item, hidden when there is nothing to scroll to
 *   [data-scroller-progress]                     bar width = visible share, position = scroll position
 */
(() => {
  if (customElements.get('femtis-scroller')) return;

  class FemtisScroller extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-scroller-track]');
      if (!this.track) return;

      this.prev = this.querySelector('[data-scroller-prev]');
      this.next = this.querySelector('[data-scroller-next]');
      this.progress = this.querySelector('[data-scroller-progress]');

      this.update = this.update.bind(this);
      this.onPrev = () => this.scrollByItem(-1);
      this.onNext = () => this.scrollByItem(1);

      this.prev?.addEventListener('click', this.onPrev);
      this.next?.addEventListener('click', this.onNext);
      this.track.addEventListener('scroll', this.update, { passive: true });

      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      this.prev?.removeEventListener('click', this.onPrev);
      this.next?.removeEventListener('click', this.onNext);
      this.track?.removeEventListener('scroll', this.update);
      this.resizeObserver?.disconnect();
    }

    scrollByItem(direction) {
      const item = this.track.firstElementChild;
      if (!item) return;

      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      this.track.scrollBy({ left: direction * (item.getBoundingClientRect().width + gap), behavior: 'smooth' });
    }

    update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const scrollable = maxScroll > 1;

      this.classList.toggle('is-scrollable', scrollable);
      if (this.prev) this.prev.hidden = !scrollable || this.track.scrollLeft <= 1;
      if (this.next) this.next.hidden = !scrollable || this.track.scrollLeft >= maxScroll - 1;

      if (this.progress) {
        const visible = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
        const position = scrollable ? this.track.scrollLeft / maxScroll : 0;
        this.progress.parentElement.hidden = !scrollable;
        this.progress.style.width = `${visible * 100}%`;
        this.progress.style.transform = `translateX(${position * ((1 - visible) / visible) * 100}%)`;
      }
    }
  }

  customElements.define('femtis-scroller', FemtisScroller);
})();
